import { Button } from '@/components/Button/Button';
import { Form, useForm } from '@/components/ui/Form';
import { Icon } from '@/components/ui/Icon';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { cn } from '@/utils/utils';
import type { Annotation } from '@anaralabs/lector';
import { ArrowUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { useDocumentAnnotations } from './use-document-annotations';

export const AnnotationNewComment = ({
  annotation,
  onClose,
}: { annotation: Annotation; onClose?: () => void }) => {
  const {
    handleAnnotationClick,
    handleUpdateAnnotation,
    handleAnnotationHover,
  } = useDocumentAnnotations(annotation.metadata?.chatbotId as string);
  const { track } = useAnalytics();

  const form = useForm({
    defaultValues: {
      comment: annotation.comment || '',
    },
    schema: z.object({
      comment: z.string(),
    }),
  });

  const [isCommentTooLong, setIsCommentTooLong] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      form.setFocus('comment');
    }, 200);
  }, [form]);

  const handleSaveComment = (data: { comment: string }) => {
    handleUpdateAnnotation(annotation.id, {
      ...annotation,
      comment: data.comment,
    });
    track(
      annotation.comment
        ? 'annotation_comment_updated'
        : 'annotation_comment_added',
      {
        location: 'pdf_viewer',
      },
    );
    handleAnnotationHover(annotation);
    handleAnnotationClick(null);
    onClose?.();
  };

  const handleTooltipClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className="flex flex-col gap-0.5 bg-popover border rounded-lg p-2 shadow-md w-[296px]"
      onClick={handleTooltipClick}
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSaveComment)}
          className="w-full"
        >
          <div
            className={cn(
              'w-full flex flex-row items-center gap-2 pl-1',
              isCommentTooLong && 'flex-col items-end',
            )}
          >
            <textarea
              {...form.register('comment')}
              className="w-full min-h-5 max-h-[200px] overflow-y-auto text-sm focus:outline-none placeholder:text-muted-foreground bg-popover resize-none"
              placeholder="Add a comment..."
              rows={1}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = `${target.scrollHeight}px`;
                setIsCommentTooLong(
                  target.value.length > 24 || target.value?.includes('\n'),
                );
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (form.formState.isValid) {
                    form.handleSubmit(handleSaveComment)();
                  }
                }
              }}
            />
            <Button
              type="submit"
              size="icon"
              className="shrink-0 h-6 w-6"
              disabled={!form.formState.isValid}
            >
              <Icon as={ArrowUp} />
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
